import React, { useState, useMemo, useEffect, useRef } from 'react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import { SingleSelect } from '@/components/common/SingleSelect';
import { ReportTable } from '@/components/reports/ReportTable';
import { useDailyEarningsPYE } from '@/api/rebates/useReports';
import useDivisions from '@/api/organization/useDivisions';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import {
  ShareNetworkIcon,
  LinkIcon,
  DownloadIcon,
} from '@phosphor-icons/react';
import { ShareReportModal } from 'src/components/reports/ShareReportModal';
import { SharedLinksManager } from 'src/components/reports/SharedLinksManager';
import { DownloadModal } from 'src/components/reports/DownloadModal';
import { downloadMultiTableCSV } from '@/utils/csvDownload';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const REPORT_ID = 'daily-earnings-pye';
const REPORT_TITLE = 'Daily Earnings PYE';

const DailyEarningsPYEPage: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const defaultYear = currentYear - 1; // Previous completed year
  const [selectedYear, setSelectedYear] = useState<number>(defaultYear);
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedCategories, setSelectedCategories] = useState<
    MultiSelectOption[]
  >([]);
  const hasSetDefaultDivision = useRef(false);
  const hasSetDefaultCategories = useRef(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linksManagerOpen, setLinksManagerOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  // Fetch divisions for filter
  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(true);

  // Fetch available years for the year selector
  const { data: availableYearsData } = useRebateAgreementYears();
  const availableYears =
    availableYearsData && 'years' in availableYearsData
      ? availableYearsData.years
      : undefined;

  // Prepare division options
  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  // Prepare year options - descending order, no future years
  const yearOptions = useMemo(() => {
    if (!availableYears || availableYears.length === 0) {
      // Fallback to last 5 years if no years available
      const years: number[] = [];
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years.map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
    }
    return availableYears
      .filter((year) => year <= currentYear) // Exclude future years
      .sort((a, b) => b - a) // Descending order
      .map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
  }, [availableYears, currentYear]);

  // Prepare category options
  const categoryOptions: MultiSelectOption[] = useMemo(
    () => [
      {
        id: 'Redistribution',
        label: 'Redistribution',
        value: 'Redistribution',
      },
      { id: 'Conditional', label: 'Conditional', value: 'Conditional' },
      {
        id: 'Non-Conditional',
        label: 'Non-Conditional',
        value: 'Non-Conditional',
      },
    ],
    [],
  );

  // Generate default filename with divisions
  const defaultFilename = useMemo(() => {
    const divisionPart =
      selectedDivisions.length > 0 &&
      selectedDivisions.length < divisionOptions.length
        ? `-${selectedDivisions.map((d) => d.label.replace(/\s+/g, '-')).join('-')}`
        : '';
    return `daily-earnings-pye-${selectedYear}${divisionPart}`;
  }, [selectedYear, selectedDivisions, divisionOptions.length]);

  // Handler for downloading all tables
  const handleDownloadClick = () => {
    if (!report) return;
    setDownloadModalOpen(true);
  };

  const handleDownload = (filename: string) => {
    if (!report) return;

    downloadMultiTableCSV({
      tables: [
        {
          title: report.table.title || 'Category Breakdown',
          table: report.table,
          rowLabelHeader: 'Division',
        },
        {
          title: report.monthlyBreakdownTable.title || 'Monthly Breakdown',
          table: report.monthlyBreakdownTable,
          rowLabelHeader: 'Division',
        },
      ],
      filename,
    });
  };

  // Default to ALL divisions when divisions are loaded (only once)
  useEffect(() => {
    if (
      divisionOptions.length > 0 &&
      selectedDivisions.length === 0 &&
      !divisionsLoading &&
      !hasSetDefaultDivision.current
    ) {
      setSelectedDivisions([...divisionOptions]);
      hasSetDefaultDivision.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisionOptions.length, divisionsLoading]);

  // Default to ALL categories (only once)
  useEffect(() => {
    if (
      categoryOptions.length > 0 &&
      selectedCategories.length === 0 &&
      !hasSetDefaultCategories.current
    ) {
      setSelectedCategories([...categoryOptions]);
      hasSetDefaultCategories.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryOptions.length]);

  // Fetch report data
  const {
    data: report,
    isLoading,
    error,
  } = useDailyEarningsPYE({
    year: selectedYear,
    divisionIds:
      selectedDivisions.length > 0
        ? selectedDivisions.map((d) => d.id)
        : undefined,
    categoryFilters:
      selectedCategories.length > 0
        ? selectedCategories.map((c) => c.id)
        : undefined,
  });

  return (
    <DashboardLayout>
      <div className="p-6 w-full min-w-0 max-w-full overflow-hidden">
        <div className="mb-6 flex flex-col gap-4">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold">Daily Earnings PYE</h1>
              <p className="text-sm text-muted-foreground mt-2">
                Post year-end earnings report showing division rebates by
                category with prior year comparison
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadClick}
                disabled={!report || isLoading}
              >
                <DownloadIcon className="h-4 w-4 mr-2" />
                Download All
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <ShareNetworkIcon className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShareModalOpen(true)}>
                    <ShareNetworkIcon className="h-4 w-4 mr-2" />
                    Create share link
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLinksManagerOpen(true)}>
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Manage shared links
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-6">
                <SingleSelect
                  label="Year"
                  labelPosition="left"
                  options={yearOptions}
                  value={
                    yearOptions.find((opt) => opt.value === selectedYear) ||
                    null
                  }
                  onChange={(option) => {
                    if (option) {
                      setSelectedYear(option.value);
                    }
                  }}
                  placeholder="Select year..."
                  searchPlaceholder="Search years..."
                  shouldAutoSort={false}
                />
                <MultiSelect
                  label="Divisions"
                  labelPosition="left"
                  options={divisionOptions}
                  value={selectedDivisions}
                  onChange={setSelectedDivisions}
                  placeholder="Select divisions..."
                  searchPlaceholder="Search divisions..."
                  disabled={divisionsLoading}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Loading State */}
        {isLoading && (
          <Card>
            <CardHeader>
              <CardTitle>Post Year-End Earnings Report</CardTitle>
            </CardHeader>
            <CardContent>
              <TableSkeleton />
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-destructive">
                Error loading report data. Please try again.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Report Tables */}
        {report && !isLoading && (
          <div className="space-y-6">
            {/* First Table: Category Breakdown */}
            <ReportTable
              table={report.table}
              timeFilter="all"
              onTimeFilterChange={() => {
                // No-op: time filter is hardcoded to "all" (full year)
              }}
              rowLabel="Division"
              showTotals={true}
              showTimeFilter={false}
            />

            {/* Second Table: Monthly Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Monthly Breakdown Filters</CardTitle>
              </CardHeader>
              <CardContent className="py-3">
                <div className="flex items-center gap-6">
                  <MultiSelect
                    label="Categories"
                    labelPosition="left"
                    options={categoryOptions}
                    value={selectedCategories}
                    onChange={setSelectedCategories}
                    placeholder="Select categories..."
                    searchPlaceholder="Search categories..."
                    liveUpdate={false}
                  />
                </div>
              </CardContent>
            </Card>
            <ReportTable
              table={report.monthlyBreakdownTable}
              timeFilter="all"
              onTimeFilterChange={() => {
                // No-op: time filter is hardcoded to "all" (full year)
              }}
              rowLabel="Division"
              showTotals={true}
              showTimeFilter={false}
            />
          </div>
        )}
      </div>

      <ShareReportModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        reportId={REPORT_ID}
        reportTitle={REPORT_TITLE}
      />
      <SharedLinksManager
        open={linksManagerOpen}
        onClose={() => setLinksManagerOpen(false)}
        reportId={REPORT_ID}
        reportTitle={REPORT_TITLE}
        onCreateNew={() => {
          setLinksManagerOpen(false);
          setShareModalOpen(true);
        }}
      />
      <DownloadModal
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        onDownload={handleDownload}
        defaultFilename={defaultFilename}
        title="Download Daily Earnings PYE"
      />
    </DashboardLayout>
  );
};

export default DailyEarningsPYEPage;
